import {gql, useApolloClient} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {Indicator, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import type {CoreObject} from 'app-core/framework/CoreObjectConstants';
import {starItems, unstarItems} from 'app-core/platform/starrable/StarDAO';
import {assert, assertAndContinue} from 'Asserts';
import {ChatType, ChatMessageOriginType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getDraftPreview, hasMeaningfulDraft, useChatDraftState} from 'llm/chat-library/ChatDraftState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import ResultsPageLoader from 'nav/sidepanel/listing/ResultsPageLoader';
import SidepanelItem from 'nav/sidepanel/listing/SidepanelItem';
import {RealtimeMessageType} from 'realtime/Constants';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import {Container} from 'ui-library/NYC';
import {readableTime} from 'util/DateUtil';
import {assertNever} from 'util/Types';

import {ChatHistoryQuery, ChatHistoryQueryVariables} from './__generated/ChatHistoryQuery';
import {getChatNameRenderInfo} from './ChatNameUtil';
import type {SidepanelChat} from './ChatRouter';
import {CHAT_SESSION_MENU_FRAGMENT} from './ChatSessionMenu';
import {RenameChatModal, updateChatSessionInCache} from './RenameChatModal';

export interface ChatSidepanelItem {
  id: string | null;
  type: ChatType | null;
  name: string | null;
  initialOrigin?: ChatMessageOriginType | null;
  modifiedAt: string;
  star?: {
    id: string;
    isStarred: boolean;
  } | null;
}

// A chat initiated by a routine (rather than a person) is surfaced with a distinct icon.
function isRoutineInitiatedChat(item: ChatSidepanelItem): boolean {
  return item.initialOrigin === ChatMessageOriginType.ROUTINE;
}

// Tooltip describing what initiated the chat. Returns null when the origin is unknown (legacy
// sessions created before initialOrigin existed), so no tooltip is shown.
function getInitialOriginTooltip(item: ChatSidepanelItem): string | null {
  const {initialOrigin} = item;
  switch (initialOrigin) {
    case ChatMessageOriginType.ROUTINE:
      return 'Triggered by Agent';
    case ChatMessageOriginType.UI:
      return 'Triggered by User';
    case ChatMessageOriginType.API:
      return 'Triggered via API';
    case ChatMessageOriginType.LLM:
      return 'Triggered by Assistant';
    case ChatMessageOriginType.SLACK_APP:
      return 'Triggered via Benchling for Slack';
    case ChatMessageOriginType.REMOTE_MCP:
      return 'Triggered via Benchling MCP';
    case ChatMessageOriginType.VOICE:
      return 'Started with Voice';
    case null:
    case undefined:
      return null;
    default:
      assertAndContinue(false, `Unexpected chat message origin: ${assertNever(initialOrigin)}`);
      return null;
  }
}

interface ChatHistoryFetchState {
  prevTokens: Array<string>;
  currentToken: string;
  nextToken: string | null;
  items: Array<ChatHistoryQuery['chatSessionListing']['items'][number]>;
  fetching: boolean;
}

const INITIAL_CHAT_HISTORY_FETCH_STATE: ChatHistoryFetchState = {
  prevTokens: [],
  currentToken: '',
  nextToken: null,
  items: [],
  fetching: false,
};

const chatHistoryFetchStateHooks = makeUseGlobalClientState<ChatHistoryFetchState>(
  INITIAL_CHAT_HISTORY_FETCH_STATE
);

export const CHAT_HISTORY_PAGE_SIZE = 10;
export const STARRED_CHAT_HISTORY_PAGE_SIZE = 5;

export const CHAT_HISTORY_QUERY = gql`
  query ChatHistoryQuery(
    $nextToken: StrictString
    $pageSize: StrictInt!
    $starred: StrictBoolean
    $automated: StrictBoolean
  ) @engComponent(name: LLM) @skipAudit {
    chatSessionListing(pageSize: $pageSize, nextToken: $nextToken, starred: $starred, automated: $automated) {
      items {
        ...ChatSessionMenu
      }
      nextToken
    }
  }
  ${CHAT_SESSION_MENU_FRAGMENT}
`;

function Timestamp(props: {timestamp: string}): JSX.Element {
  const formattedTime = useMemo(() => readableTime(props.timestamp), [props.timestamp]);
  return <>{formattedTime}</>;
}

function DraftIndicator({chatId}: {chatId: string | null}): JSX.Element | null {
  const [draftState] = useChatDraftState(chatId);
  const draftPreview = getDraftPreview(draftState);
  if (!draftPreview) {
    return null;
  }
  return (
    <Tooltip label={`Draft: ${draftPreview}`} openDelay={1000} withinPortal={true}>
      <span className={css(styles.draftIndicator)}>
        <BenchlingIcon icon="edit" />
      </span>
    </Tooltip>
  );
}

export function ChatHistoryItemIcon({
  iconSize = 13,
  indicatorSize = 8,
  item,
  isUnread = false,
}: {
  iconSize?: number;
  indicatorSize?: number;
  item: ChatSidepanelItem;
  isUnread?: boolean;
}): JSX.Element {
  const icon = (
    <Indicator
      color={Color.BLUE_400}
      disabled={!isUnread}
      inline={true}
      offset={3}
      position="top-start"
      size={indicatorSize}
    >
      <BenchlingIcon
        icon={isRoutineInitiatedChat(item) ? 'robot-outline' : 'comment-outline'}
        style={{display: 'block', height: iconSize, width: iconSize}}
      />
    </Indicator>
  );
  const tooltip = getInitialOriginTooltip(item);
  if (tooltip == null) {
    return icon;
  }
  return (
    <Tooltip label={tooltip} openDelay={1000} withinPortal={true}>
      {icon}
    </Tooltip>
  );
}

export function ChatHistoryItem(props: {
  className?: string;
  item: ChatSidepanelItem;
  onStarUpdated: () => Promise<void>;
  onSelect: (chat: SidepanelChat) => void;
  isSelected: boolean;
}): JSX.Element {
  const {item} = props;
  const fuzzyDate = useFuzzyDate(item.modifiedAt);
  const isStarred = Boolean(item.star?.isStarred);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUpdatingStar, setIsUpdatingStar] = useState(false);
  const apolloClient = useApolloClient();

  const chatNameInfo = getChatNameRenderInfo(item);

  const onChatNameUpdated = useCallback(
    (message: {data: {name: string; modifiedAt: string}}) => {
      if (item.id != null) {
        updateChatSessionInCache(apolloClient, item.id, message.data.name, message.data.modifiedAt);
      }
    },
    [apolloClient, item.id]
  );

  useRealtimeChannelSubscription(
    item.id != null ? getRealtimeChannelNameForApiId(item.id) : '',
    onChatNameUpdated,
    [RealtimeMessageType.CHAT_NAME_UPDATED]
  );

  const openRenameModal = useCallback(() => {
    assert(item.id != null, 'User attempted to rename a non-persisted chat session');
    const key = `rename-chat-${item.id}`;
    LegacyModalContainer.open(
      key,
      <RenameChatModal
        chatId={item.id}
        currentName={chatNameInfo.text}
        onClose={() => {
          LegacyModalContainer.close(key);
        }}
      />
    );
  }, [item.id, chatNameInfo.text]);

  const handleClickStar = useCallback(async () => {
    if (item.id == null || isUpdatingStar) {
      return;
    }
    const nextIsStarred = !isStarred;
    const toggleStar = nextIsStarred ? starItems : unstarItems;
    setIsUpdatingStar(true);
    try {
      await toggleStar([{id: item.id as CoreObject['id']}]);
      await props.onStarUpdated();
    } finally {
      setIsUpdatingStar(false);
    }
  }, [isStarred, isUpdatingStar, item.id, props]);

  return (
    <SidepanelItem
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      className={classNames(props.className, css(styles.chatHistoryItemRoot))}
      dataTestAttrs={{
        component: 'ChatHistoryItem',
        element: 'root',
        key: item.name ?? '',
        ...(props.isSelected ? {index: 'active'} : {}),
      }}
      icon={<ChatHistoryItemIcon item={item} />}
      isCompact={false}
      title={{
        text: chatNameInfo.text,
        node: <span className={css(styles.chatNameText)}>{chatNameInfo.node}</span>,
      }}
      showStar={item.id != null}
      starred={isStarred}
      handleClickStar={handleClickStar}
      inlineContentRight={
        item.id != null ? (
          <span
            className={css(
              styles.overflowMenuWrapper,
              !isHovering && !isMenuOpen && styles.overflowMenuHidden
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <OverflowMenu
              buttonProps={{'aria-label': 'Chat options', className: css(styles.roundButton)}}
              isIconHorizontal={false}
              isOpen={isMenuOpen}
              menuProps={{dropLeft: true, width: MenuWidth.FIT_CONTENT}}
              onOpenChange={setIsMenuOpen}
              options={[
                {
                  icon: 'edit',
                  contents: <>Rename</>,
                  onSelect: openRenameModal,
                },
              ]}
            />
          </span>
        ) : null
      }
      subTitle={
        <>
          <span>Last modified </span>
          <Tooltip label={<Timestamp timestamp={item.modifiedAt} />} openDelay={1000} withinPortal={true}>
            <span>{fuzzyDate}</span>
          </Tooltip>
          <DraftIndicator chatId={item.id} />
        </>
      }
      onClick={() => {
        props.onSelect(item.id != null ? {id: item.id, type: item.type} : {id: null});
      }}
      isExpandable={false}
      isChild={false}
      active={props.isSelected}
    />
  );
}

interface ChatListingProps {
  fetchNext: (() => void) | null;
  fetchPrevious: (() => void) | null;
  onStarUpdated: () => Promise<void>;
  onSelectChatSession: (chat: SidepanelChat) => void;
  selectedChatSession: SidepanelChat | null;
}

function PresentationalChatListing(props: ChatListingProps & {items: Array<ChatSidepanelItem>}): JSX.Element {
  // Subscribe to new chat draft to determine if we should show placeholder
  const [newChatDraftState] = useChatDraftState(null);
  const hasNewChatDraft = hasMeaningfulDraft(newChatDraftState);

  // Show new chat placeholder if:
  // 1. Currently viewing a new chat (selectedChatSession.id == null), OR
  // 2. There's a meaningful draft for new chat and we're on the first page
  const placeholderItems = useMemo(() => {
    const shouldShowPlaceholder =
      (props.selectedChatSession && props.selectedChatSession.id == null && props.fetchPrevious == null) ||
      (hasNewChatDraft && props.fetchPrevious == null);

    if (!shouldShowPlaceholder) {
      return [];
    }

    return [
      {
        id: null,
        name: null,
        type: newChatDraftState.settings?.agentType
          ? getChatTypeForNewAgentChat(newChatDraftState.settings.agentType)
          : null,
        modifiedAt: new Date().toISOString(),
        star: null,
      },
    ];
  }, [props.selectedChatSession, props.fetchPrevious, hasNewChatDraft, newChatDraftState]);
  const allItems = useMemo(() => [...placeholderItems, ...props.items], [placeholderItems, props.items]);
  if (allItems.length === 0) {
    return (
      <div className={css(styles.emptyChatListing)}>
        <div className={css(styles.emptyChatListingMessage)}>
          <p className={css(styles.emptyChatListingMessageTitle)}>No chats found</p>
          <p>
            Start a new chat with the <BenchlingIcon icon="plus" /> icon above.
          </p>
        </div>
      </div>
    );
  }
  return (
    <Container size="sidepanel" dataTestAttrs={{component: 'ChatListing', element: 'root'}}>
      {props.fetchPrevious && (
        <ResultsPageLoader isPrevious={true} limit={2} setOffset={props.fetchPrevious} offset={0} />
      )}
      {allItems.map((item, idx) => {
        if (item.id == null) {
          return (
            <ChatHistoryItem
              className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
              key="new-chat"
              item={item}
              onStarUpdated={props.onStarUpdated}
              onSelect={props.onSelectChatSession}
              isSelected={props.selectedChatSession?.id === item.id}
            />
          );
        }
        return (
          <ChatHistoryItem
            className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
            key={item.id}
            item={item}
            onStarUpdated={props.onStarUpdated}
            onSelect={props.onSelectChatSession}
            isSelected={props.selectedChatSession?.id === item.id}
          />
        );
      })}
      {props.fetchNext && (
        <ResultsPageLoader isPrevious={false} limit={2} setOffset={props.fetchNext} offset={0} />
      )}
    </Container>
  );
}

type ChatHistoryItemData = ChatHistoryQuery['chatSessionListing']['items'][number];

/** Drop chat items that share an id, keeping the first occurrence. Used when accumulating pages. */
function dedupeChatItemsById(items: Array<ChatHistoryItemData>): Array<ChatHistoryItemData> {
  const seenIds = new Set<string>();
  const deduped: Array<ChatHistoryItemData> = [];
  for (const item of items) {
    if (item.id != null) {
      if (seenIds.has(item.id)) {
        continue;
      }
      seenIds.add(item.id);
    }
    deduped.push(item);
  }
  return deduped;
}

export interface AccumulatingChatHistory {
  /** Every chat item loaded so far, across all pages fetched via {@link loadMore}. */
  items: Array<ChatHistoryItemData>;
  /** True while the first page is loading and there is nothing to display yet. */
  isInitialLoading: boolean;
  /** True when the query errored with nothing to display. */
  isError: boolean;
  /** True while a subsequent ("View more") page is loading. */
  isLoadingMore: boolean;
  /** True while the first page is refreshing with stale items still displayed. */
  isRefreshing: boolean;
  /** Whether another page can be loaded. */
  hasMore: boolean;
  /** Load and append the next page, or null when there is no next page. */
  loadMore: (() => void) | null;
  /** Immediately hide an item while the filtered query refreshes. */
  removeItem: (itemId: string) => void;
  /** Reset to the first page and refetch. */
  refresh: () => Promise<void>;
  /** Refetch the first page after an error. */
  retry: () => void;
}

export interface ChatHistoryFilters {
  starred: boolean;
  automated?: boolean | null;
}

/**
 * Chat-history fetching for the redesigned sidebar (LLM_ENABLE_CHAT_REDESIGN). Unlike the legacy
 * prev/next pager in {@link ChatListing}, this accumulates pages so a single "View more" affordance
 * appends older chats. Reuses CHAT_HISTORY_QUERY and the shared item shape; per-item realtime name
 * updates continue to be handled inside {@link ChatHistoryItem}.
 */
export function useAccumulatingChatHistory(
  {starred, automated = null}: ChatHistoryFilters,
  pageSize: number,
  {enabled = true}: {enabled?: boolean} = {}
): AccumulatingChatHistory {
  // Items folded in from previously-loaded pages. The page currently being fetched is appended on
  // top of this at render time so the list never flickers or shrinks while the next page loads.
  const [previousPageItems, setPreviousPageItems] = useState<Array<ChatHistoryItemData>>([]);
  const [currentToken, setCurrentToken] = useState('');
  const [hiddenItemIds, setHiddenItemIds] = useState<ReadonlySet<string>>(() => new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const result = useSkippableBenchlingQuery<ChatHistoryQuery, ChatHistoryQueryVariables>(CHAT_HISTORY_QUERY, {
    skip: !enabled,
    variables: {nextToken: currentToken, pageSize, starred, automated},
    fetchPolicy: 'network-only',
    renderCachedDataWhenRefetching: true,
  });

  const isSuccess = result.type === QueryResultType.SUCCESS;
  const nextToken = isSuccess ? result.data.chatSessionListing.nextToken : null;

  const items = useMemo(() => {
    const currentPageItems =
      result.type === QueryResultType.SUCCESS ? result.data.chatSessionListing.items : [];
    return dedupeChatItemsById([...previousPageItems, ...currentPageItems]).filter(
      (item) => item.id == null || !hiddenItemIds.has(item.id)
    );
  }, [hiddenItemIds, previousPageItems, result]);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }
    setIsRefreshing(true);
    if (result.type === QueryResultType.SUCCESS) {
      setPreviousPageItems((previousItems) =>
        dedupeChatItemsById([...previousItems, ...result.data.chatSessionListing.items])
      );
    }
    setCurrentToken('');
    try {
      await result.refetch({nextToken: '', pageSize, starred, automated});
      setPreviousPageItems([]);
      setHiddenItemIds(new Set());
    } finally {
      setIsRefreshing(false);
    }
  }, [automated, enabled, pageSize, result, starred]);

  const removeItem = useCallback((itemId: string) => {
    setHiddenItemIds((currentIds) => new Set(currentIds).add(itemId));
  }, []);

  const loadMore = useMemo(() => {
    if (!isRefreshing && isSuccess && nextToken != null && nextToken !== '') {
      return () => {
        setPreviousPageItems((prev) =>
          dedupeChatItemsById([...prev, ...result.data.chatSessionListing.items])
        );
        setCurrentToken(nextToken);
      };
    }
    return null;
  }, [isRefreshing, isSuccess, nextToken, result]);
  const isLoading =
    result.type === QueryResultType.PENDING || (result.type === QueryResultType.SUCCESS && result.loading);

  return {
    items,
    isInitialLoading: result.type === QueryResultType.PENDING && items.length === 0,
    // Only a first-page failure is fatal. A failed "View more" keeps the already-loaded chats
    // visible rather than replacing the whole list with an error.
    isError: result.type === QueryResultType.ERROR && previousPageItems.length === 0,
    isLoadingMore: isLoading && !isRefreshing && previousPageItems.length > 0,
    isRefreshing,
    hasMore: !isRefreshing && nextToken != null && nextToken !== '',
    loadMore,
    removeItem,
    refresh,
    retry: () => {
      if (!enabled) {
        return;
      }
      setPreviousPageItems([]);
      setCurrentToken('');
      result.refetch({nextToken: '', pageSize, starred, automated});
    },
  };
}

export default function ChatListing({
  onSelectChatSession,
  selectedChatSession,
  refetchRef,
}: {
  onSelectChatSession: (chat: SidepanelChat) => void;
  selectedChatSession: SidepanelChat | null;
  refetchRef: React.MutableRefObject<() => void>;
}): JSX.Element {
  const [fetchState, setFetchState] = chatHistoryFetchStateHooks.useGlobalClientState(
    'ai-sidepanel-chat-history-list'
  );

  const result = useBenchlingQuery<ChatHistoryQuery, ChatHistoryQueryVariables>(CHAT_HISTORY_QUERY, {
    variables: {
      nextToken: fetchState.currentToken,
      pageSize: CHAT_HISTORY_PAGE_SIZE,
      starred: null,
      automated: null,
    },
    fetchPolicy: 'network-only',
  });

  const refetchChatHistory = useCallback(async () => {
    setFetchState({...INITIAL_CHAT_HISTORY_FETCH_STATE, fetching: true});
    try {
      const refetchResult = await result.refetch({
        nextToken: INITIAL_CHAT_HISTORY_FETCH_STATE.currentToken,
        pageSize: CHAT_HISTORY_PAGE_SIZE,
        starred: null,
        automated: null,
      });
      setFetchState({
        ...INITIAL_CHAT_HISTORY_FETCH_STATE,
        nextToken: refetchResult.data.chatSessionListing.nextToken,
        items: refetchResult.data.chatSessionListing.items,
      });
    } catch (e) {
      setFetchState({...fetchState, fetching: false});
      throw e;
    }
  }, [fetchState, result, setFetchState]);

  useEffect(() => {
    refetchRef.current = () => {
      setFetchState(INITIAL_CHAT_HISTORY_FETCH_STATE);
      result.refetch();
    };
  }, [setFetchState, refetchRef, result]);

  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS) {
      // We keep track of just the item ids, to encourage the item to read directly from the cache,
      setFetchState({
        prevTokens: fetchState.prevTokens,
        currentToken: fetchState.currentToken,
        nextToken: result.data.chatSessionListing.nextToken,
        items: result.data.chatSessionListing.items,
        fetching: false,
      });
    }
  }, [result, setFetchState, fetchState.currentToken, fetchState.prevTokens]);

  const fetchNext = useMemo(() => {
    if (result.type === QueryResultType.SUCCESS && result.data.chatSessionListing.nextToken) {
      return () => {
        setFetchState({
          prevTokens: [...fetchState.prevTokens, fetchState.currentToken],
          currentToken: result.data.chatSessionListing.nextToken,
          nextToken: null,
          items: fetchState.items,
          fetching: true,
        });
      };
    } else {
      return null;
    }
  }, [result, setFetchState, fetchState]);

  const fetchPrevious = useMemo(() => {
    if (fetchState.prevTokens.length > 0) {
      return () => {
        setFetchState({
          prevTokens: fetchState.prevTokens.slice(0, -1),
          currentToken: fetchState.prevTokens[fetchState.prevTokens.length - 1],
          nextToken: null,
          items: fetchState.items,
          fetching: false,
        });
      };
    } else {
      return null;
    }
  }, [fetchState, setFetchState]);

  const sharedListProps = {
    fetchNext,
    fetchPrevious,
    onStarUpdated: refetchChatHistory,
    onSelectChatSession,
    selectedChatSession,
  } satisfies ChatListingProps;
  switch (result.type) {
    case QueryResultType.SUCCESS:
      return <PresentationalChatListing {...sharedListProps} items={result.data.chatSessionListing.items} />;
    case QueryResultType.ERROR:
      return (
        <Container size="sidepanel" className={css(styles.errorContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Error loading chats."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Retry',
              actionOnClick: () => {
                result.refetch();
              },
              rightAlign: true,
            }}
          />
        </Container>
      );
    case QueryResultType.PENDING:
      return (
        <div style={{opacity: 0.5}}>
          {fetchState.items.length ? (
            <PresentationalChatListing {...sharedListProps} items={fetchState.items} />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </div>
      );
    default:
      throw new Error(`Unknown result type: ${assertNever(result).type}`);
  }
}

const styles = StyleSheet.create({
  chatHistoryItemRoot: {
    position: 'relative',
  },
  firstChatHistoryItem: {
    borderTop: `1px solid transparent`,
  },
  emptyChatListing: {
    flex: 0.3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '20px',
  },
  emptyChatListingMessageTitle: {
    color: Color.GRAY_600,
    fontSize: '18px',
  },
  emptyChatListingMessage: {
    color: Color.GRAY_500,
    fontSize: '14px',
    lineHeight: '20px',
  },
  errorContainer: {
    padding: '12px',
  },
  draftIndicator: {
    marginLeft: '8px',
    color: Color.GRAY_500,
    display: 'inline-flex',
    alignItems: 'center',
  },
  overflowMenuWrapper: {
    position: 'absolute' as const,
    right: 32,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
  },
  overflowMenuHidden: {
    visibility: 'hidden' as const,
    pointerEvents: 'none' as const,
  },
  roundButton: {
    borderRadius: '50%',
  },
  chatNameText: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
